import React, { useState, useEffect } from "react";
import "./style.css";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ForgotPassword from "./components/ForgotPassword";
import {
  getCaptchaSiteKey,
  fetchHtml,
  parseFragmentHtml,
  injectFragmentStyles,
} from "./utils/index";
import { useCssReady } from "./utils/useCssReady";

interface HeaderData {
  header: string;
  headerScript: HTMLScriptElement[];
}

const App: React.FC = () => {
  const [headerData, setHeaderData] = useState<HeaderData>({
    header: "",
    headerScript: [],
  });
  const [footerData, setFooterData] = useState("");
  const [siteKey, setSiteKey] = useState("");
  // Stays false until every stylesheet on the page has loaded, so Header/Footer
  // can hold off rendering their real markup (avoiding an unstyled icon flash).
  const cssReady = useCssReady();
  // Track each fragment's styles independently so the header and footer do not
  // block one another while their external content is loading.
  const [headerCssReady, setHeaderCssReady] = useState(false);
  const [footerCssReady, setFooterCssReady] = useState(false);

  async function fetchHeaderData(headerUrl: string) {
    try {
      const headerText = await fetchHtml(headerUrl);
      const headerFragment = parseFragmentHtml(headerText);

      setHeaderData({
        header: headerFragment.bodyHtml,
        headerScript: headerFragment.scripts,
      });

      await injectFragmentStyles(
        headerFragment.styleHrefs,
        headerFragment.styleTags
      );
    } catch (error) {
      console.error(error);
    } finally {
      setHeaderCssReady(true);
    }
  }

  async function fetchFooterData(footerUrl: string) {
    try {
      const footerText = await fetchHtml(footerUrl);
      const footerFragment = parseFragmentHtml(footerText);

      setFooterData(footerFragment.bodyHtml);

      await injectFragmentStyles(
        footerFragment.styleHrefs,
        footerFragment.styleTags
      );
    } catch (error) {
      console.error(error);
    } finally {
      setFooterCssReady(true);
    }
  }

  useEffect(() => {
    const fetchSiteKey = async () => {
      // getCaptchaSiteKey resolves headerUrl/footerUrl from site-config,
      // falling back to the env-configured URLs itself when that fails
      const result = await getCaptchaSiteKey();

      // Start the external header and footer paths independently before
      // mounting the captcha. Neither fragment waits for the other or for
      // Turnstile to load and validate.
      void fetchHeaderData(result.headerUrl);
      void fetchFooterData(result.footerUrl);
      setSiteKey(result.siteKey);
    };

    fetchSiteKey();
  }, []);

  const headerContentCssReady = cssReady && headerCssReady;
  const footerContentCssReady = cssReady && footerCssReady;

  return (
    <>
      <Router>
        <Header
          dataHtml={headerData.header}
          headerScript={headerData.headerScript}
          cssReady={headerContentCssReady}
        />
        <Routes>
          <Route
            path="/:locale/forgot-password"
            element={siteKey && <ForgotPassword siteKey={siteKey} />}
          />
        </Routes>
        <Footer dataHtml={footerData} cssReady={footerContentCssReady} />
      </Router>
    </>
  );
};

export default App;
